/* import __COLOCATED_TEMPLATE__ from './semifinal-qual-startlist-interface.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export default class HeatStartlistComponent extends Component 
{
	@service store;
	@service csv;
	@service sprintTools;
	
	@tracked sourceEvent;
	@tracked sourceClassGroup;

	@tracked bibOffset = 0;
	@tracked resultsOffset = 0;

	// Class filters - when checked, only athletes with matching className are included
	@tracked filterU14 = false;
	@tracked filterU16 = false;
	@tracked filterU18 = false;
	@tracked filterU20 = false;

	get hasClassFilter() {
		return this.filterU14 || this.filterU16 || this.filterU18 || this.filterU20;
	}

	get selectedClassFilters() {
		const filters = [];
		if (this.filterU14) filters.push('U14');
		if (this.filterU16) filters.push('U16');
		if (this.filterU18) filters.push('U18');
		if (this.filterU20) filters.push('U20');
		return filters;
	}

	get classFilterDisplay() {
		if (!this.hasClassFilter) return '';
		return this.selectedClassFilters.join(', ');
	}

	get resultsRangeStart() {
		return (parseInt(this.resultsOffset) || 0) + 1;
	}

	get resultsRangeEnd() {
		return (parseInt(this.resultsOffset) || 0) + 12;
	}

	@tracked S1ClassGroup;
	@tracked S2ClassGroup;

	@tracked nextResults;
	
	@tracked S1Assignments;
	@tracked S2Assignments;
	
	@tracked startTimeS1;
	@tracked startTimeS2;
	
	@action
	async sourceEventChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.sourceEvent = null;
			this.sourceClassGroup = null;
		}
		else
		{
			var event = await this.store.findRecord('Event', browserEvent.target.value);
			this.sourceEvent = event;
			this.sourceClassGroup = null;
		}
		
		this.updateResults();
	}
	
	@action
	async classGroupChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.sourceClassGroup = null;
			this.updateResults();
		}
		else
		{
			var classGroup = await this.store.findRecord('ClassGroup', browserEvent.target.value);
			this.sourceClassGroup = classGroup;
		}
		
		this.updateResults();
	}
	
	@action
	bibOffsetChanged(browserEvent)
	{
		browserEvent.preventDefault();
		this.updateHeatAssignments();
	}

	@action
	resultsOffsetChanged(browserEvent)
	{
		browserEvent.preventDefault();
		this.updateResults();
	}

	@action
	toggleClassFilter(filterName)
	{
		if (filterName === 'U14') this.filterU14 = !this.filterU14;
		else if (filterName === 'U16') this.filterU16 = !this.filterU16;
		else if (filterName === 'U18') this.filterU18 = !this.filterU18;
		else if (filterName === 'U20') this.filterU20 = !this.filterU20;
		this.updateResults();
	}

	@action
	tieSelectionChanged(result, browserEvent)
	{
		result.set('useResultForTie', browserEvent.target.checked);
		this.updateHeatAssignments();
	}
	
	@action
	async S1ClassGroupChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.S1ClassGroup = null;
		}
		else
		{
			var classGroup = await this.store.findRecord('ClassGroup', browserEvent.target.value);
			this.S1ClassGroup = classGroup;
			this.startTimeS1 = dayjs(classGroup.startTime).format("HH:mm:ss");
			this.updateHeatAssignments();
		}
	}
	
	@action
	async S2ClassGroupChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.S2ClassGroup = null;
		}
		else
		{
			var classGroup = await this.store.findRecord('ClassGroup', browserEvent.target.value);
			this.S2ClassGroup = classGroup;
			this.startTimeS2 = dayjs(classGroup.startTime).format("HH:mm:ss");
			this.updateHeatAssignments();
		}
	}
	
	@action
	exportHeatStartListCSV()
	{
		// Check if assignments exist before exporting
		if (!this.S1Assignments || !this.S2Assignments) {
			return;
		}

		var buildData = (assignments, classGroup, startTime) => {
			var data = [...this.sprintTools.getCSVHeaders()];
			for (const result of assignments) {
				data.push(this.sprintTools.buildCSVRow(result, classGroup, startTime, result.newBib));
			}
			return data;
		};

		this.csv.export(buildData(this.S1Assignments, this.S1ClassGroup, this.startTimeS1), {fileName:'S1quarterfinalsStartlist.csv',withSeparator:false});
		this.csv.export(buildData(this.S2Assignments, this.S2ClassGroup, this.startTimeS2), {fileName:'S2quarterfinalsStartlist.csv',withSeparator:false});
	}
	
	@action
	async createClasses()
	{
		var destinationEvent = this.args.destinationEvent;
		var destinationClasses = await destinationEvent.get('classes');

		await this.sprintTools.createClassesForHeat(destinationEvent, destinationClasses, this.S1ClassGroup, this.S1Assignments);
		await this.sprintTools.createClassesForHeat(destinationEvent, destinationClasses, this.S2ClassGroup, this.S2Assignments);
	}
	
	@action
	async createEntries()
	{
		var destinationEvent = this.args.destinationEvent;
		var destinationEntries = await destinationEvent.get('entries');
		var destinationClasses = await destinationEvent.get('classes');

		await this.sprintTools.createEntriesForHeat(destinationEvent, destinationEntries, destinationClasses, this.S1ClassGroup, this.S1Assignments, this.startTimeS1);
		await this.sprintTools.createEntriesForHeat(destinationEvent, destinationEntries, destinationClasses, this.S2ClassGroup, this.S2Assignments, this.startTimeS2);
	}
	
	updateHeatAssignments()
	{	
		
		var bibOffset = parseInt(this.bibOffset);
		
		//only assign heats if all class groups have valid selections
		if(	this.S1ClassGroup == null || 
			this.S2ClassGroup == null)
		{
			return;
		}
		
		//only assign heats if the top30Results is populated fully
		if(this.nextResults == null)// || this.top30Results.length != 30)
		{
			return;
		}
		
		var S1Assignments = [];
		var S2Assignments = [];
		
		//S1: 1,4,5,8,9,12
		if(this.nextResults.length > 0)
		{
			this.nextResults[0].set('newBib',1+bibOffset);
			this.nextResults[0].set('heatStartOrder',1);
			S1Assignments.push(this.nextResults[0]);
		}
		if(this.nextResults.length > 3)
		{
			this.nextResults[3].set('newBib',4+bibOffset);
			this.nextResults[3].set('heatStartOrder',2);
			S1Assignments.push(this.nextResults[3]);
		}
		if(this.nextResults.length > 4)
		{
			this.nextResults[4].set('newBib',5+bibOffset);
			this.nextResults[4].set('heatStartOrder',3);
			S1Assignments.push(this.nextResults[4]);
		}
		if(this.nextResults.length > 7)
		{
			this.nextResults[7].set('newBib',8+bibOffset);
			this.nextResults[7].set('heatStartOrder',4);
			S1Assignments.push(this.nextResults[7]);
		}
		if(this.nextResults.length > 8)
		{
			this.nextResults[8].set('newBib',9+bibOffset);
			this.nextResults[8].set('heatStartOrder',5);
			S1Assignments.push(this.nextResults[8]);
		}
		if(this.nextResults.length > 11)
		{
			this.nextResults[11].set('newBib',12+bibOffset);
			this.nextResults[11].set('heatStartOrder',6);
			S1Assignments.push(this.nextResults[11]);
		}
		
		//S2: 2,3,6,7,10,11
		if(this.nextResults.length > 1)
		{
			this.nextResults[1].set('newBib',2+bibOffset);
			this.nextResults[1].set('heatStartOrder',1);
			S2Assignments.push(this.nextResults[1]);
		}
		if(this.nextResults.length > 2)
		{
			this.nextResults[2].set('newBib',3+bibOffset);
			this.nextResults[2].set('heatStartOrder',2);
			S2Assignments.push(this.nextResults[2]);
		}
		if(this.nextResults.length > 5)
		{
			this.nextResults[5].set('newBib',6+bibOffset);
			this.nextResults[5].set('heatStartOrder',3);
			S2Assignments.push(this.nextResults[5]);
		}
		if(this.nextResults.length > 6)
		{
			this.nextResults[6].set('newBib',7+bibOffset);
			this.nextResults[6].set('heatStartOrder',4);
			S2Assignments.push(this.nextResults[6]);
		}
		if(this.nextResults.length > 9)
		{
			this.nextResults[9].set('newBib',10+bibOffset);
			this.nextResults[9].set('heatStartOrder',5);
			S2Assignments.push(this.nextResults[9]);
		}
		if(this.nextResults.length > 10)
		{
			this.nextResults[10].set('newBib',11+bibOffset);
			this.nextResults[10].set('heatStartOrder',6);
			S2Assignments.push(this.nextResults[10]);
		}
		
		this.S1Assignments = S1Assignments;
		this.S2Assignments = S2Assignments;
	}
	
	async updateResults()
	{
		if(this.sourceClassGroup == null)
		{
			this.nextResults = null;
			this.updateHeatAssignments();
			return;
		}

		var nextResults = [];
		var resultsOffset = parseInt(this.resultsOffset) || 0;
		var hasClassFilter = this.hasClassFilter;
		var selectedClassFilters = this.selectedClassFilters;

		await this.sprintTools.fetchResultsForEvent(this.sourceEvent.id);

		var results = await this.sourceClassGroup.get('resultsSorted');

		var count = 0;
		var skipped = 0;
		for (const result of results)
		{
			// Skip results based on offset FIRST (from full results)
			if(skipped < resultsOffset)
			{
				skipped++;
				continue;
			}

			// Then apply class filter if active
			if (hasClassFilter)
			{
				if (!selectedClassFilters.includes(result.className))
				{
					continue;
				}
			}

			if(count < 12)
			{
				count++;
				result.set('useResultForTie',false);
				nextResults.push(result);
			}
		}

		// Sort by time, break ties by lowest SP points
		nextResults.sort((a, b) => this.sprintTools.sortByTimeWithTieBreaker(a, b));

		this.nextResults = nextResults;
		this.updateHeatAssignments();
	}
}
